import React, { createRef, useCallback } from 'react';
import * as Styles from './DnsCard.styles';
import {
  Alert,
  Button,
  IconDomain, IconFilledInfo, Tag, Tooltip,
} from 'gatorcomponents';
import { useSelector } from 'react-redux';
import { useFeatureIsOn, useFeatureValue } from '@growthbook/growthbook-react';
import { AI_AGENTS_DOMAINS_PAGE, ATIVACAO_N8N_INHOUSE } from '@/config/GrowthBook/constants';
import { PopoverContent } from '@/components/Popover/PopoverContent';
import { Popover } from '@/components/Popover';
import { useAiChat } from '@/hooks/AiChat';

const DnsCard = ({
  manageDomainHandlerLocale,
  isInPropagation,
  propagationStatus,
  detailState,
  isPointedToReserved,
  redirectToDnsWizard,
}) => {
  const guideRef = createRef(null);
  const { isIframeOpen } = useSelector(state => state.merlin);
  const showAiAgentButton = useFeatureIsOn(AI_AGENTS_DOMAINS_PAGE);
  const n8nInhouse = useFeatureValue(ATIVACAO_N8N_INHOUSE);

  const { handleInitialInteraction } = useAiChat();

  const handleRedirect = () => {
    redirectToDnsWizard && redirectToDnsWizard();
  };

  const getPopoverProps = useCallback(() => ({
    title: manageDomainHandlerLocale.cardOptions.dns.popover.title,
    description: manageDomainHandlerLocale.cardOptions.dns.popover.description,
    buttonLabel: manageDomainHandlerLocale.cardOptions.dns.popover.buttonLabel,
  }), [manageDomainHandlerLocale]);

  const popoverContent = <PopoverContent {...getPopoverProps()} />;

  const steps = [
    {
      popover: {
        key: 'domainsBannerButton',
        content: popoverContent,
        pointer: 'topRight',
        $maxWidth: 240,
      },
      mask: {
        customRadius: 100,
      },
    },
  ];

  const popoverStorageKey = 'notShowConfigureDomainPopover';
  const notShowBannerPopover = localStorage.getItem(popoverStorageKey) || isIframeOpen || !showAiAgentButton;

  const openIframe = useCallback(() => {
    if (n8nInhouse && n8nInhouse.domains) {
      handleInitialInteraction();
    }
  }, [handleInitialInteraction, n8nInhouse]);

  return (
    <>
      {!notShowBannerPopover && <Popover steps={steps} guideRef={guideRef} storageKey={popoverStorageKey} />}
      <Styles.CardTitle>
        <IconDomain />
        {manageDomainHandlerLocale.cardOptions.dns.title}

        <Tooltip content={manageDomainHandlerLocale.cardOptions.dns.tooltip}>
          <IconFilledInfo color="primaryBlue" />
        </Tooltip>

        {isInPropagation && (
        <Styles.TagWrapperDesktop>
          <Tag
            variant="pending"
            label={manageDomainHandlerLocale.tags[propagationStatus.status]}
            testId={`${propagationStatus.status}-tag`}
          />
        </Styles.TagWrapperDesktop>
        )}
      </Styles.CardTitle>

      {isInPropagation && (
        <Styles.TagWrapperMobile>
          <Tag
            variant="pending"
            label={manageDomainHandlerLocale.tags[propagationStatus.status]}
            testId={`${propagationStatus.status}-tag`}
          />
        </Styles.TagWrapperMobile>
      )}

      <Styles.DnsContent>
        <>
          <Styles.DnsSpan data-testid="first-dns">
            {detailState.nameservers && detailState.nameservers.length
              ? `${detailState.nameservers[0]} `
              : '- '
          }
            <Styles.SlashSpan>/</Styles.SlashSpan>
          </Styles.DnsSpan>
          <Styles.DnsSpan data-testid="second-dns">
            {detailState.nameservers && detailState.nameservers.length
              ? `${detailState.nameservers[1]}`
              : '- '
          }
          </Styles.DnsSpan>
        </>
      </Styles.DnsContent>

      {isInPropagation && (
      <Styles.NoPaddingAlertWrapper>
        <Alert
          variant="info"
          withoutBackground
          description={manageDomainHandlerLocale.cardOptions.dns.alert[propagationStatus.status]}
          testId="in_progress-alert"
        />
      </Styles.NoPaddingAlertWrapper>
      )}

      {isPointedToReserved && (
      <Alert
        variant="warning"
        description={manageDomainHandlerLocale.cardOptions.dns.alert.reserved}
        testId="reserved-alert"
      />
      )}

      <Styles.ButtonsWrapper>
        <Button
          label={manageDomainHandlerLocale.cardOptions.dns.cta}
          onClick={handleRedirect}
          testId="dnsCta"
          variant="secondary"
          width="fit"
        />

        {showAiAgentButton && (
          <Styles.AiButtonWrapper ref={guideRef}>
            <Button
              label={manageDomainHandlerLocale.cardOptions.dns.useAiButtonLabel}
              onClick={openIframe}
              variant="tertiary"
              width="fit"
            />
          </Styles.AiButtonWrapper>
        )}
      </Styles.ButtonsWrapper>
    </>
  );
};

export default DnsCard;
